import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  ShieldCheck, Trash2, TimerReset, User as UserIcon, RefreshCw, Loader2,
  MessageSquare, FileText, MessageCircle, Inbox,
} from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const FILTERS = [
  { id: "all", label: "All", icon: Inbox },
  { id: "posts", label: "Posts", icon: FileText },
  { id: "comments", label: "Comments", icon: MessageSquare },
  { id: "chat", label: "Chat", icon: MessageCircle },
];

const TYPE_BADGE = {
  post: "bg-copper/15 text-copper",
  comment: "bg-sage/25 text-bronze",
  chat: "bg-penny/20 text-bronze",
};

function timeAgo(iso) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ""; }
}

export default function GuardianPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const isMod = !!(user && (user.is_guardian || user.role === "admin"));

  const load = useCallback((f) => {
    setLoading(true);
    api.get(`/guardian/feed?filter=${f}&limit=80`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (isMod) load(filter); }, [filter, isMod, load]);

  if (user && !isMod) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <ShieldCheck className="w-10 h-10 text-copper mx-auto mb-3" />
        <p className="text-muted-foreground">This area is for Community Guardians and moderators only.</p>
      </div>
    );
  }

  const removeItem = (it) => setItems((prev) => prev.filter((x) => x.id !== it.id));

  const del = async (it) => {
    try {
      if (it.type === "post") await api.delete(`/posts/${it.post_id}`);
      else if (it.type === "comment") await api.delete(`/posts/${it.post_id}/comment/${it.comment_id}`);
      else await api.delete(`/chat/${it.id}`);
      removeItem(it);
      toast.success("Content removed 💛");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doSuspend = async () => {
    if (!suspendTarget) return;
    setBusy(true);
    try {
      await api.post(`/moderate/suspend/${suspendTarget.author_id}`, { hours: 24, reason: reason || "Community guideline violation" });
      toast.success(`${suspendTarget.author_name} was given a 24h cool-down.`);
      setSuspendTarget(null); setReason("");
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="guardian-dashboard-container" className="max-w-3xl mx-auto px-3.5 sm:px-6 py-6 sm:py-8 flex flex-col items-center">
      <div className="flex flex-col items-center text-center mb-1">
        <p className="font-mono text-xs uppercase tracking-widest text-copper flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Guardian
        </p>
        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-bronze mt-1">Watch feed</h1>
        <div className="w-20 h-1 bg-gradient-to-r from-copper via-penny to-sage rounded-full my-2.5 opacity-90" />
      </div>
      <p className="text-muted-foreground text-sm mb-4 text-center max-w-xl">The newest posts, comments and chat — scan and act fast. Delete rude content or issue a 24h cool-down.</p>
      <Button data-testid="guardian-refresh-btn" onClick={() => load(filter)} variant="outline" size="sm"
        className="rounded-full min-h-[40px] mb-5">
        <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
      </Button>

      {/* Filter tabs */}
      <div className="flex items-center gap-1.5 p-1.5 rounded-2xl bg-secondary/60 border border-border/80 overflow-x-auto scrollbar-none mb-5 w-full max-w-md">
        {FILTERS.map((f) => {
          const active = filter === f.id;
          return (
            <button key={f.id} data-testid={`guardian-filter-tab-${f.id}`} onClick={() => setFilter(f.id)}
              className={`flex-1 min-h-[40px] px-3 py-1.5 rounded-xl text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                active ? "bg-white text-copper font-semibold shadow-sm" : "text-foreground/70 hover:text-foreground"
              }`}>
              <f.icon className="w-3.5 h-3.5" /> {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading feed…</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16 px-4 space-y-3">
          <ShieldCheck className="w-10 h-10 text-sage" />
          <p className="text-muted-foreground">Nothing to review right now. The community is peaceful 💛</p>
        </div>
      ) : (
        <div className="space-y-3 w-full max-w-xl" data-testid="guardian-feed-list">
          {items.map((it) => (
            <div key={`${it.type}-${it.id}`} data-testid={`guardian-item-${it.type}-${it.id}`}
              className="p-3.5 sm:p-5 rounded-2xl sm:rounded-3xl bg-card border border-border/90 shadow-sm space-y-3 hover:border-bronze/30 transition-all">
              <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-2.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <img src={fileUrl(it.author_avatar) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${it.author_id}`}
                    alt="" className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-secondary shrink-0 object-cover" />
                  <div className="min-w-0">
                    <Link to={`/u/${it.author_id}`} className="font-medium text-sm text-foreground truncate hover:text-copper block max-w-[45vw] sm:max-w-none">{it.author_name}</Link>
                    <p className="font-mono text-[11px] text-muted-foreground truncate">
                      {timeAgo(it.created_at)}{it.type === "chat" && it.channel ? ` · #${it.channel}` : ""}
                    </p>
                  </div>
                </div>
                <span className={`font-mono text-[10px] sm:text-xs px-2.5 py-0.5 rounded-full font-bold uppercase shrink-0 ${TYPE_BADGE[it.type]}`}>{it.type}</span>
              </div>

              {it.content ? (
                <p className="text-sm text-foreground/90 leading-normal break-words bg-secondary/40 p-2.5 sm:p-3 rounded-xl border border-border/40 line-clamp-4">{it.content}</p>
              ) : (
                <p className="text-xs text-muted-foreground italic px-1">No text content.</p>
              )}
              {it.media_url && (
                <img src={fileUrl(it.media_url)} alt="" className="rounded-xl max-h-40 w-auto border border-border/40" />
              )}

              <div className="grid grid-cols-3 gap-2 pt-1 border-t border-border/40">
                <Button data-testid={`guardian-action-delete-${it.id}`} onClick={() => del(it)}
                  variant="ghost" size="sm"
                  className="min-h-[40px] rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200/60 text-xs font-medium">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
                <Button data-testid={`guardian-action-suspend-${it.id}`}
                  onClick={() => { setSuspendTarget(it); setReason(""); }}
                  variant="ghost" size="sm"
                  className="min-h-[40px] rounded-xl bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200/60 text-xs font-medium">
                  <TimerReset className="w-3.5 h-3.5 mr-1" /> 24h
                </Button>
                <Button data-testid={`guardian-action-view-${it.id}`} onClick={() => navigate(`/u/${it.author_id}`)}
                  variant="ghost" size="sm"
                  className="min-h-[40px] rounded-xl bg-secondary/70 text-foreground hover:bg-secondary border border-border text-xs font-medium">
                  <UserIcon className="w-3.5 h-3.5 mr-1" /> Author
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!suspendTarget} onOpenChange={(o) => !o && setSuspendTarget(null)}>
        <DialogContent data-testid="guardian-confirm-modal" className="sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-bronze flex items-center gap-2">
              <TimerReset className="w-5 h-5 text-amber-500" /> Issue a 24h cool-down
            </DialogTitle>
            <DialogDescription className="sr-only">Confirm a 24-hour cool-down for this member.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{suspendTarget?.author_name}</span> won't be able to post, comment or chat for 24 hours. They can still browse. This is logged.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-widest text-copper font-mono">Reason (optional)</Label>
              <Input data-testid="guardian-suspend-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Repeated rudeness in #general" className="rounded-xl mt-1" />
            </div>
          </div>
          <DialogFooter className="grid grid-cols-2 gap-2.5">
            <Button variant="outline" onClick={() => setSuspendTarget(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="guardian-suspend-confirm" onClick={doSuspend} disabled={busy}
              className="rounded-full bg-amber-500 hover:bg-amber-500/90 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm cool-down"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
